import { useCallback, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useAnalytics } from '../../../controllers/analytics';
import {
	createResultFieldDiagnostics,
	onResultFieldChangesObserved,
	type ResultEdges,
} from './result-field-diagnostics';

type OnResultsAnalyticsRun = (state: {
	isLoadingNext: boolean;
	shouldReportResults: boolean;
}) => void;

export const useResultFieldDiagnostics = (
	edges: ResultEdges,
	primaryProduct: string | undefined,
): OnResultsAnalyticsRun => {
	const { fireAnalyticsEvent } = useAnalytics();
	const enabled =
		primaryProduct === 'jira' &&
		fg('rovo_fps_results_analytics_field_guard') &&
		fg('rovo_fps_result_field_diagnostics');
	// Disabling diagnostics discards the baseline; the report budget remains shared across mounts.
	const diagnostics = useMemo(
		() => (enabled ? createResultFieldDiagnostics() : undefined),
		[enabled],
	);

	return useCallback<OnResultsAnalyticsRun>(
		({ isLoadingNext, shouldReportResults }) => {
			if (!diagnostics) {
				return;
			}
			if (isLoadingNext) {
				diagnostics.reset();
				return;
			}
			try {
				const summary = diagnostics.observe({
					edges,
					resetBaseline: shouldReportResults,
					now: Date.now(),
				});
				if (summary) {
					fireAnalyticsEvent(onResultFieldChangesObserved(summary));
				}
			} catch {
				// Optional diagnostics must not interrupt search or emit a second error-event burst.
				diagnostics.reset();
			}
		},
		[diagnostics, edges, fireAnalyticsEvent],
	);
};
